import { Injectable, Injector } from '@angular/core';
import { EventParticipationCountsResponse } from '@bling-fe/shared/repositories';
import { ArticleDetail } from '../../core/declarations/article-detail.interface';
import { ArticleDocument } from '../../core/declarations/article.interface';
import { Like } from '../../core/declarations/like.interface';
import { PaginatedResponse } from '../../core/declarations/paginated.interface';
import {
  Article,
  ArticleDeleteParams,
  ArticleFilterParams,
  ArticleParams
} from '../../core/declarations/article.interface';
import {
  DefineRepositoryParams,
  Identifiable,
  RepositoryAbstract
} from './repository.abstract';
import { Observable } from 'rxjs';

export interface ArticleBookmarkResponse {
  message: string;
  bookmarked: boolean;
}

export type ArticleRepositoryParams = DefineRepositoryParams<
  ArticleDetail,
  {
    queryParams: ArticleFilterParams;
    createParams: ArticleParams;
    updateParams: ArticleParams & Identifiable;
    deleteParams: ArticleDeleteParams;
    all: PaginatedResponse<Article>;
  }
>;

@Injectable({
  providedIn: 'root'
})
export class ArticleRepository extends RepositoryAbstract<ArticleRepositoryParams> {
  constructor(injector: Injector) {
    super(`/articles`, injector);
  }

  like(id: number): Observable<Like> {
    return this.http.post<Like>(`${this.RESOURCE_URL}/${id}/likes`, null);
  }

  findDocument(
    articleId: number,
    documentId: number
  ): Observable<ArticleDocument> {
    return this.http.get<ArticleDocument>(
      `${this.RESOURCE_URL}/${articleId}/documents/${documentId}`
    );
  }

  vote(id: number, option: number): Observable<ArticleDetail> {
    return this.http.post<ArticleDetail>(
      `${this.RESOURCE_URL}/${id}/vote/${option}`,
      {}
    );
  }

  pin(id: number): Observable<ArticleDetail> {
    return this.http.get<ArticleDetail>(
      `${this.RESOURCE_URL}/${id}/toggle_sticky`
    );
  }

  /**
   * Exports event participants for a given article (event) as a XSLX blob.
   */
  exportEventParticipants(id: number): Observable<Blob> {
    return this.http.get(
      `${this.RESOURCE_URL}/${id}/event_participations/export`,
      { responseType: 'blob' }
    );
  }

  /**
   * It returns counts of going and interested participants for a given article id.
   */
  counts(id: number): Observable<EventParticipationCountsResponse> {
    return this.http.get<EventParticipationCountsResponse>(
      `${this.RESOURCE_URL}/${id}/event_participations/counts`
    );
  }

  /**
   * It returns likes for a given article id.
   */
  getLikes(id: number): Observable<Like[]> {
    return this.http.get<Like[]>(`${this.RESOURCE_URL}/${id}/likes`);
  }
}
